import { userLoginAction } from "@/api/user/userLogin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import talyko from "@/assets/TalykoLogo.png";
import loginImage from "@/assets/loginImage.png";

import { z } from "zod";

const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});
type LoginSchemaType = z.infer<typeof LoginSchema>;
export function LoginPage() {
  const navigate = useNavigate();

  const { register, handleSubmit, reset } = useForm<LoginSchemaType>({
    resolver: zodResolver(LoginSchema),
  });

  const { mutateAsync: LoginFunction } = useMutation({
    mutationKey: ["login"],
    mutationFn: userLoginAction,
    retry: 5,
  });

  async function handleLoginSubmit(data: LoginSchemaType) {
    try {
      await LoginFunction({
        email: data.email,
        password: data.password,
      });
      reset();
      navigate("/dashboard");
      toast.success("Login Success");
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="mx-auto flex h-screen w-full flex-col justify-center">
      {/* Top section: Logo and Links */}
      <div className="flex w-full items-center justify-between px-3 py-4 md:px-8">
        {/* Left: Logo */}
        <div className="flex items-center">
          <img className="h-16" src={talyko} alt="Talyko Logo" />
        </div>

        {/* Right: About and Help Links */}
        <div className="mt-1 flex items-center space-x-8 md:text-lg">
          <a href="#" className="text-black hover:underline">
            About
          </a>
          <a href="#" className="text-black hover:underline">
            Help
          </a>
        </div>
      </div>

      {/* Bottom section: Main Content */}
      <div className="relative flex flex-grow flex-col lg:flex-row">
        {/* Left Column for Image and SVG Wave */}
        <div className="relative hidden w-full items-center justify-center overflow-hidden lg:flex lg:h-auto lg:w-1/2">
          {/* SVG Background Curve */}
          <div className="absolute inset-0 hidden lg:block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 800 800"
              className="h-full w-full"
              preserveAspectRatio="none"
            >
              <path
                fill="#A2D8D8"
                fill-opacity="0.5"
                stroke="#000000"
                stroke-width="1"
                d="M 0 0 C 500 150, 200 400, 800 800 L 0 800 Z"
              />
            </svg>
          </div>

          {/* Add your logo and image */}
          <img
            src={loginImage}
            className="relative z-10 max-h-[80%]"
            alt="login image"
          />
        </div>

        {/* Right Column for Login Card */}
        <div className="my-auto w-full items-center justify-center px-5 md:my-auto lg:w-1/2">
          <form onSubmit={handleSubmit(handleLoginSubmit)}>
            <Card className="relative mx-auto h-[641px] space-y-7 overflow-hidden rounded-[2.5rem] border border-black p-5 font-Hemmers text-black md:w-[450px] xl:w-[500px]">
              <div className="absolute inset-0 h-10 w-full rounded-tl-full rounded-tr-full bg-[#F8CF2C] p-0">
                <div className="absolute inset-6 inset-x-5 flex items-center justify-end gap-4">
                  <span className="flex size-3 items-center rounded-full bg-black" />
                  <span className="flex size-3 items-center rounded-full bg-black" />
                  <span className="flex size-3 items-center rounded-full bg-black" />
                </div>
              </div>
              <CardHeader className="flex items-center text-3xl xl:text-4xl">
                Login
              </CardHeader>
              <CardContent>
                {/* Email and password  */}
                <div className="w-full space-y-4">
                  <Input
                    className="bg-[#ECECEC]"
                    placeholder="Email"
                    {...register("email")}
                  />
                  <Input
                    className="bg-[#ECECEC]"
                    placeholder="Password"
                    type="password"
                    {...register("password")}
                  />
                </div>
                {/* Forgot Password */}
                <div className="mt-2 flex justify-end">
                  <a href="#" className="text-sm text-gray-500 hover:underline">
                    Forgot Password?
                  </a>
                </div>
                {/* Log in button */}
                <div className="flex items-center justify-center">
                  <Button
                    className="mt-7 w-full max-w-[264px] bg-blue-950 hover:bg-blue-900"
                    type="submit"
                  >
                    Log in
                  </Button>
                </div>
                {/* Or component */}
                <div className="mt-9 flex w-full items-center">
                  <div className="h-px flex-grow bg-gray-300"></div>
                  <span className="px-4 text-gray-500">or</span>
                  <div className="h-px flex-grow bg-gray-300"></div>
                </div>
                {/* Google sign-in button */}
                <div className="mt-9 flex flex-col items-center space-y-4">
                  <button className="flex items-center justify-center space-x-2 rounded-full border border-gray-300 bg-white px-6 py-2 shadow-md hover:bg-gray-100">
                    <img
                      src="https://developers.google.com/identity/images/g-logo.png" // Official Google logo
                      alt="Google Logo"
                      className="h-6 w-6" // Adjusted size for the logo
                    />
                    <Link
                      to={"https://invoicing-app.onrender.com/oauth2/google"}
                      className="text-base font-medium text-gray-700"
                      style={{ fontFamily: "Roboto, sans-serif" }}
                    >
                      Sign in with Google
                    </Link>
                  </button>
                </div>

                {/* Pushes "Create an account" down */}
                <div className="mt-16 flex flex-col items-center space-y-4">
                  <div className="text-center text-gray-500">
                    New here?{" "}
                    <a
                      href="/users"
                      className="font-semibold text-blue-950 hover:text-blue-900 hover:underline"
                    >
                      Create an account
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </div>
    </div>
  );
}
